import { Element } from "@xmpp/xml"
import i18n from "@/lib/i18n/i18n"
import {
  NAVIGATOR_TIMELINE_NS,
  type NavigatorTimelineFeed,
} from "@/lib/navigatorTimeline"
import { type GboxTimelineEvent } from "@/lib/gboxTimeline"

const COMMAND_NS = "http://jabber.org/protocol/commands"

const toOptionalNumber = (value?: string) => {
  if (!value) return undefined
  const parsed = Number(value)
  return Number.isNaN(parsed) ? undefined : parsed
}

const toOptionalString = (value?: string) => value || undefined

const toBoolean = (value?: string) => value === "1"

const parseEvent = (eventElement: Element): GboxTimelineEvent => ({
  eventId: eventElement.getAttr("place_id") || "",
  errandId: eventElement.getAttr("transport_id") || "",
  chainId: eventElement.getAttr("chain_id") || "",
  kind:
    (eventElement.getAttr("kind") as GboxTimelineEvent["kind"] | undefined) ||
    "other",
  kindLabel:
    eventElement.getAttr("kind_label") || i18n.t("planner.parsers.stopKind"),
  title: eventElement.getAttr("title") || i18n.t("planner.parsers.stopTitle"),
  subtitle: toOptionalString(eventElement.getAttr("subtitle") || undefined),
  customer: toOptionalString(eventElement.getAttr("customer") || undefined),
  referenceNumber: toOptionalString(
    eventElement.getAttr("reference_number") || undefined,
  ),
  countryCode: toOptionalString(
    eventElement.getAttr("country_code") || undefined,
  ),
  scheduledTime: toOptionalString(
    eventElement.getAttr("scheduled_time") || undefined,
  ),
  realTime: toOptionalString(eventElement.getAttr("real_time") || undefined),
  estimatedArrivalTime: toOptionalString(
    eventElement.getAttr("estimated_arrival_time") || undefined,
  ),
  estimatedArrivalTimeWithTacho: toOptionalString(
    eventElement.getAttr("estimated_arrival_time_with_tacho") || undefined,
  ),
  timelinessStatus: toOptionalString(
    eventElement.getAttr("timeliness_status") || undefined,
  ),
  delaySeconds: toOptionalNumber(
    eventElement.getAttr("delay_seconds") || undefined,
  ),
  delayWithTachoSeconds: toOptionalNumber(
    eventElement.getAttr("delay_with_tacho_seconds") || undefined,
  ),
  sequence: toOptionalNumber(eventElement.getAttr("sequence") || undefined),
  externalStatus: toOptionalString(
    eventElement.getAttr("external_status") || undefined,
  ),
  errandStatusLabel: undefined,
  isCurrentErrand: false,
  isCurrentEvent: false,
  isCompleted: toBoolean(eventElement.getAttr("is_completed") || undefined),
  isOnRoute: toBoolean(eventElement.getAttr("is_on_route") || undefined),
})

const parseFeed = (feedElement: Element): NavigatorTimelineFeed => ({
  chatJid: toOptionalString(feedElement.getAttr("chat_jid") || undefined),
  deviceId: feedElement.getAttr("vehicle_id") || "",
  plateNumber: toOptionalString(
    feedElement.getAttr("plate_number") || undefined,
  ),
  generatedAt: toOptionalString(
    feedElement.getAttr("generated_at") || undefined,
  ),
  events: feedElement
    .getChildren("event", NAVIGATOR_TIMELINE_NS)
    .map(parseEvent)
    .filter((event) => Boolean(event.eventId && event.errandId)),
})

const NavigatorTimelineParser = {
  parse(stanza: Element): NavigatorTimelineFeed[] {
    const commandElement = stanza.getChild("command", COMMAND_NS)
    if (!commandElement) {
      return []
    }

    return commandElement
      .getChildren("navigator-timeline", NAVIGATOR_TIMELINE_NS)
      .map(parseFeed)
      .filter((feed) => feed.deviceId.length > 0)
  },
}

export default NavigatorTimelineParser
